"use client";

import { useCallback, useEffect, useRef, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import Link from "@/components/ui/LocaleLink";
import { useTranslations } from "next-intl";
import { BookOpen, Globe, Info, MagnifyingGlass, Newspaper, SignIn, SignOut, SquaresFour } from "@phosphor-icons/react/dist/ssr";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import SparklesIcon from "@/components/ui/SparklesIcon";
import { ROLE_DASHBOARD, type Role } from "@/lib/auth-config";
import { logoutAction } from "@/lib/actions/auth";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";
import { DESKTOP_MQ } from "@/lib/breakpoints";
import { nextTrapIndex } from "@/lib/focus-trap";

/** Open/close transition, mirrored in the CSS below. */
const ANIM_MS = 220;

interface MobileMenuProps {
  role: Role | null;
  /** Assistant row. Gated by the caller on the same three things as
   *  <NavChatLink> — env, audience (admin or client), live mode. */
  showChat?: boolean;
}

/**
 * The mobile + tablet navigation, folded into one white dropdown under the
 * header. Replaces the bottom bar below `lg`; the desktop header is untouched.
 * Approved mockup: `design/burger-nav-mockup.html`.
 *
 * ⛔ WHY THE OVERLAY IS A PORTAL, AND WHY THE CLOSE BUTTON IS DRAWN TWICE.
 * `NavScrollWrapper` renders the header `position: fixed` with a `transform`
 * (its hide-on-scroll). Both of those make it a CONTAINING BLOCK for fixed
 * descendants AND a stacking context at z-index 50 — so a scrim rendered inside
 * the header could neither cover the viewport (it would be clipped to the
 * header's own box) nor sit above the header's own pixels. The overlay
 * therefore portals to <body> at `z-overlay`, which is what dims the header
 * along with the page.
 *
 * That same stacking context is why the burger cannot simply be lifted above
 * the scrim: nothing inside a z-50 context can paint above a z-200 sibling of
 * that context, at any z-index of its own. So while the menu is open the
 * header's own button is hidden and the portal draws the button again, `fixed`
 * at the measured coordinates of the one it replaced. One button is visible at
 * any moment, in the same place, and the bars morph into the X on a shared
 * origin — `armed` flips one frame after mount so the transition actually runs
 * rather than the portal appearing already-crossed.
 */
export default function MobileMenu({ role, showChat = false }: MobileMenuProps) {
  const t = useTranslations("nav");
  const reducedMotion = useReducedMotion();

  // `mounted` = the portal exists; `armed` = it is showing its open state.
  // Two flags rather than one so the panel can animate OUT before unmounting.
  const [mounted, setMounted] = useState(false);
  const [armed, setArmed] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether THIS close should hand focus back to the trigger. False when the
  // reader is leaving anyway (a link) or when the trigger is about to vanish
  // (a resize past `lg`) — focusing a control that is gone is worse than not.
  const restoreFocus = useRef(false);

  const open = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setRect(triggerRef.current?.getBoundingClientRect() ?? null);
    setMounted(true);
    if (reducedMotion) { setArmed(true); return; }
    requestAnimationFrame(() => requestAnimationFrame(() => setArmed(true)));
  }, [reducedMotion]);

  const close = useCallback((returnFocus = false) => {
    restoreFocus.current = returnFocus;
    setArmed(false);
    if (reducedMotion) { setMounted(false); return; }
    closeTimer.current = setTimeout(() => setMounted(false), ANIM_MS);
  }, [reducedMotion]);

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  /**
   * ⛔ FOCUS, BOTH WAYS — measured broken before this existed. The trigger goes
   * `invisible` while the menu is open, which BLURS it and drops it from the
   * tab order, so opening left focus on <body>: the next Tab went to the top of
   * the page rather than into the menu just opened. Closing was the same bug
   * from the other end — the old Escape handler focused the trigger while it
   * was still invisible, which silently does nothing.
   *
   * So: focus the portalled close button on open, and hand focus back only
   * AFTER the trigger is visible again (i.e. once `mounted` is false), which is
   * why this keys on the unmount rather than on the click.
   */
  useEffect(() => {
    if (mounted) { closeRef.current?.focus(); return; }
    if (!restoreFocus.current) return;
    restoreFocus.current = false;
    triggerRef.current?.focus();
  }, [mounted]);

  // Escape, and a viewport that grows past `lg` — the trigger is `lg:hidden`,
  // so without this a resize would strand an open panel with nothing to shut it.
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { close(true); return; }
      // ⛔ `aria-modal` DOES NOT TRAP KEYBOARD FOCUS — it only tells assistive
      // tech the rest of the page is inert. Measured with the menu open: 10
      // tabbables inside the dialog, 111 still reachable outside it, so Tab
      // from the last row walked straight out into a page the scrim makes
      // unclickable. Claiming modality while not behaving modally is worse
      // than the un-trapped panel this replaced, so the claim has to be made
      // true. Wraps in both directions.
      // ⚠️ Gated on `armed`, NOT `mounted`. `mounted` stays true through the
      // 220ms exit, and trapping during it pulls focus BACK into a dialog that
      // is disappearing — measured, and most visible after a link click, where
      // focus jumped backwards into a fading overlay on the next page.
      if (e.key !== "Tab" || !armed || !dialogRef.current) return;
      const items = [...dialogRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])")];
      const target = nextTrapIndex(items.length, items.indexOf(document.activeElement as HTMLElement), e.shiftKey);
      if (target === null) return;   // an interior move: leave it to the browser
      e.preventDefault();
      items[target].focus();
    };
    const mq = window.matchMedia(DESKTOP_MQ);
    const onDesktop = () => { if (mq.matches) close(); };
    // ⚠️ The close button is drawn at the trigger's MEASURED coordinates, so a
    // viewport change while open strands it: measured 437px of drift rotating
    // 375x812 → 812x420, with the button left floating mid-screen. Only a
    // resize past `lg` closes the menu, so every rotation below it has to
    // re-measure. Same rect-plus-listener shape `Select` and `ShareButton` use.
    const onResize = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      // ⚠️ An EMPTY box is not a position. Crossing `lg` makes the trigger
      // `display:none`, and getBoundingClientRect then returns all ZEROS
      // rather than null — measured, that parked a 34px close button in the
      // top-left corner for ~150ms while the panel animated out. Treat a
      // zero-width box as "nowhere" so the button simply stops rendering.
      setRect(r && r.width > 0 ? r : null);
    };
    document.addEventListener("keydown", onKey);
    mq.addEventListener("change", onDesktop);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      document.removeEventListener("keydown", onKey);
      mq.removeEventListener("change", onDesktop);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [mounted, armed, close]);

  // Hold the page still underneath. The panel is fixed, so without this the
  // content scrolls behind it and the scrim's edges drift off the viewport.
  useEffect(() => {
    if (!mounted) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [mounted]);

  const dashboard = role ? ROLE_DASHBOARD[role] : null;
  // Each role's dashboard is a different place and says so, matching
  // <AccountDropdown> rather than inventing a fourth word for it.
  const dashboardLabel =
    role === "professional" ? t("proDashboard")
    : role === "admin" ? t("adminPanel")
    : t("yourDashboard");
  const links: { href: string; label: string; icon: React.ReactNode }[] = [
    // Signed in → their own dashboard leads. Absent for a signed-out reader,
    // so the owner-approved public order is unchanged for them.
    ...(dashboard ? [{ href: dashboard, label: dashboardLabel, icon: <SquaresFour size={20} weight="regular" aria-hidden="true" /> }] : []),
    { href: "/search",        label: t("findProfessional"), icon: <MagnifyingGlass size={20} weight="regular" aria-hidden="true" /> },
    { href: "/how-it-works",  label: t("howItWorks"),       icon: <Info size={20} weight="regular" aria-hidden="true" /> },
    { href: "/insights",      label: t("insights"),         icon: <Newspaper size={20} weight="regular" aria-hidden="true" /> },
    { href: "/guides",        label: t("guides"),           icon: <BookOpen size={20} weight="regular" aria-hidden="true" /> },
    ...(showChat ? [{ href: "/chat", label: t("aiChat"), icon: <SparklesIcon size={17} /> }] : []),
  ];

  const rowClass =
    "flex items-center gap-13 rounded-control px-13 py-13 text-sm font-medium leading-tight text-navy transition-colors hover:bg-navy/[0.05] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold";
  const buttonBase =
    "flex items-center justify-center gap-8 whitespace-nowrap rounded-control px-13 py-13 text-xs font-bold uppercase tracking-[0.04em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold";

  // Three bars on one origin — the outer two rotate into the cross while the
  // middle drops out, so the burger and the X are the same object rather than
  // two glyphs traded underneath each other.
  const bars = (crossed: boolean) => (
    <span aria-hidden="true" className="relative block h-13 w-17">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="absolute inset-x-0 h-2 rounded-full bg-current"
          style={{
            top: i * 5.5,
            opacity: crossed && i === 1 ? 0 : 1,
            transform: crossed
              ? i === 0 ? "translateY(5.5px) rotate(45deg)"
                : i === 1 ? "scaleX(0.4)"
                : "translateY(-5.5px) rotate(-45deg)"
              : undefined,
            transition: reducedMotion ? "none" : `transform ${ANIM_MS}ms cubic-bezier(.2,0,0,1), opacity 140ms ease`,
          }}
        />
      ))}
    </span>
  );

  const triggerClass =
    "grid h-34 w-34 place-items-center rounded-control border border-navy/15 bg-white text-navy transition-colors hover:border-navy/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold lg:hidden";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (mounted ? close(true) : open())}
        aria-expanded={mounted}
        aria-label={mounted ? t("closeMenu") : t("openMenu")}
        // Hidden, not unmounted: it keeps its place in the header row so the
        // portalled twin has coordinates to sit at and nothing reflows.
        className={`${triggerClass} ${mounted ? "invisible" : ""}`}
      >
        {bars(false)}
      </button>

      {mounted && createPortal(
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={t("mobileNav")}
          className="fixed inset-0 z-overlay"
        >
          {/* Dims the page AND the header — see the portal note above. */}
          <div
            onClick={() => close(true)}
            aria-hidden="true"
            className="absolute inset-0 bg-navy/30"
            style={{ opacity: armed ? 1 : 0, transition: reducedMotion ? "none" : "opacity 200ms ease" }}
          />


          {/* The trigger, redrawn above the scrim at the coordinates of the one
              it replaced — the only way a control inside the header's stacking
              context can stay lit while the header itself is dimmed. */}
          {rect && (
            <button
              ref={closeRef}
              type="button"
              onClick={() => close(true)}
              aria-label={t("closeMenu")}
              className={`${triggerClass.replace(" lg:hidden", "")} fixed z-top`}
              style={{ top: rect.top, left: rect.left }}
            >
              {bars(armed)}
            </button>
          )}

          <nav
            className="fixed inset-x-8 z-overlay-menu overflow-hidden rounded-pop border border-card-line bg-white shadow-e5 sm:left-auto sm:right-8 sm:w-377"
            style={{
              top: rect ? rect.bottom + 8 : 64,
              opacity: armed ? 1 : 0,
              transform: armed ? "translateY(0)" : "translateY(-8px)",
              transition: reducedMotion ? "none" : `opacity 180ms ease, transform ${ANIM_MS}ms cubic-bezier(.2,0,0,1)`,
            }}
          >
            <div className="flex flex-col gap-3 p-13 pt-16">
              {links.map(({ href, label, icon }, i) => (
                <Fragment key={href}>
                  <Link href={href} onClick={() => close()} className={rowClass}>
                    <span className="shrink-0 text-navy">{icon}</span>
                    {label}
                  </Link>
                  {/* Signed in, the first row is the reader's own place (pro
                      dashboard, client dashboard, admin panel) and the rest is
                      the public site. A hairline between them says so
                      (owner, 2026-09-13, for every signed-in role). */}
                  {dashboard && i === 0 && (
                    <hr className="mx-13 my-5 border-0 border-t border-navy/10" />
                  )}
                </Fragment>
              ))}
              {/* In the list, per the approved order — but filled, because a
                  plain row would read as a fifth destination rather than the
                  action the business runs on.

                  Guests and CLIENTS only, mirroring the desktop header: a
                  professional already has a listing and an admin is not the
                  audience, and neither is offered it on desktop. */}
              {(!role || role === "client") && (
                <Link
                  href="/pricing"
                  onClick={() => close()}
                  className={`${buttonBase} mt-8 bg-gold text-navy hover:bg-gold-hover`}
                >
                  {t("listServices")}
                </Link>
              )}
            </div>

            {/* The rule separates what you came to do from who you are. */}
            <div className="flex flex-col gap-3 border-t border-navy/10 bg-surface p-13">
              {/* Same width as the session button below (no side padding of its
                  own), with clear air under it, and a visible "Language" label
                  top left that also names the group (owner, 2026-09-23). */}
              <div className="flex flex-col gap-8 pt-8 pb-21">
                <p className="flex items-center gap-5 text-2xs font-semibold uppercase tracking-widest text-gray-500">
                  <Globe size={13} weight="bold" aria-hidden="true" className="shrink-0" />
                  <span id="mobile-menu-language">{t("languageHeading")}</span>
                </p>
                <LanguageSwitcher variant="segmented" labelledBy="mobile-menu-language" />
              </div>
              {dashboard ? (
                /* Only the session action lives down here — the dashboard is
                   the menu's first row now. A real form POSTing the SAME
                   server action the desktop dropdown uses, not a link:
                   sign-out is a mutation, and one implementation beats two
                   ways to end a session. Without it there is no way to sign
                   out below `lg` at all, since the account dropdown that
                   carried it does not render there. */
                <form action={logoutAction} className="contents">
                  <button
                    type="submit"
                    onClick={() => close()}
                    className={`${buttonBase} mt-5 w-full border border-navy/20 bg-white text-navy hover:bg-navy/[0.05]`}
                  >
                    <SignOut size={17} weight="bold" aria-hidden="true" />
                    {t("signOut")}
                  </button>
                </form>
              ) : (
                <Link
                  href="/login"
                  onClick={() => close()}
                  className={`${buttonBase} mt-5 border border-navy/20 bg-white text-navy hover:bg-navy/[0.05]`}
                >
                  <SignIn size={17} weight="bold" aria-hidden="true" />
                  {t("login")}
                </Link>
              )}
            </div>
          </nav>

        </div>,
        document.body,
      )}
    </>
  );
}
